import { RootState } from "../../../redux-store";
import { createSelector } from "@reduxjs/toolkit";
import { selectProductGroups } from "../teams/selectors";
import { selectMyTeamsIds } from "../../auth/selectors";
import { PricingAgreement } from ".";

// Stable empty reference: a fresh [] per call makes useSelector re-render the
// component on every store update.
const NO_PRICING_AGREEMENTS: PricingAgreement[] = [];

export const selectProduct = (id?: string) => (state: RootState) =>
  id && state.data.products.entities[id];

export const selectSurcharge = (id?: string) => (state: RootState) =>
  id && state.data.products.surchargeEntities[id];

export const selectProductMap = (state: RootState) =>
  state.data.products.entities;

export const selectSurchargeMap = (state: RootState) =>
  state.data.products.surchargeEntities;

export const selectProducts = createSelector(
  selectProductMap,
  (state: RootState) => state.data.products.ids,
  (entities, ids) =>
    ids
      .map((id) => entities[id])
      .sort((a, b) => Number(a.productNumber) - Number(b.productNumber))
);

export const selectProductsForDispatcherTeamProductGroups = createSelector(
  selectProductMap,
  selectProductGroups,
  selectMyTeamsIds,
  (state: RootState) => state.data.products.ids,
  (products, productGroups, myTeamIds, productIds) => {
    const visibleGroups = productGroups.filter((x) =>
      x.teamIds.find((y) => myTeamIds.includes(y))
    );
    const visibleGroupsIds = visibleGroups.map((g) => g.id);
    return productIds
      .map((id) => products[id])
      .sort((a, b) => Number(a.productNumber) - Number(b.productNumber))
      .filter(
        (p) => p.productGroupId && visibleGroupsIds.includes(p.productGroupId)
      );
  }
);

export const selectPricingAgreementsForClient =
  (clientId?: string) => (state: RootState) => {
    const featureEnabled =
      state.features.features["pricing-agreements"]?.enabled;
    if (!featureEnabled || !clientId) {
      return NO_PRICING_AGREEMENTS;
    }
    return (
      state.data.products.pricingAgreements[clientId] || NO_PRICING_AGREEMENTS
    );
  };

export const selectSurcharges = (state: RootState) =>
  state.data.products.surcharges;
